import React, {useState, useEffect, useCallback } from 'react';
import { Button,ProgressBar } from '@blueprintjs/core';
import Corpus from './corpus/Corpus.js';
import Search from './search/Search.js';
import ResultTabItem from './ResultTabItem.js';
import Read from './results/read/Read.js';
import History from './results/history/History';
import { ROOT_PATH_REGEX } from '../constants';
import { Tab, Tabs } from '@blueprintjs/core';
import { useTranslation } from 'react-i18next';
import { CORPUS_TYPES } from './corpus/constants';
import Cookies from 'js-cookie';
import { FRONT_USER_FRONT_LOGGED_IN_COOKIE_NAME} from '../auth/constants.js';
import DisplaySent from './results/concord/DisplaySent.js';
import AdvancedParams from './search/params/AdvancedParams.js';
import { computeLLR, rounded }  from '../utils.js';
//import { prepCoocSpec, prepStatsData, prepConcordData, prepReadyData, prepChronoData} from "./results/prepData";
import './dashboard.css';


// new version of functional component

export default function Dashboard () {
  const { t }=useTranslation();

  const [corpus,setCorpus]=useState(null);
  const [params,setParams]=useState(null);
  const [generalize,setGeneralize]=useState(null);
  const [completeSearch]=useState(false);
  const [displayData,setDisplayData]=useState({});
  const [pageData,setPageData]=useState(null);
  const [userFrontLoggedIn]=useState(!!(Cookies.get(FRONT_USER_FRONT_LOGGED_IN_COOKIE_NAME)));
  const [page,setPage]=useState('corpus');
  const [queryHistory,setQueryHistory]=useState([]);
  const [currentQueryNum,setCurrentQueryNum]=useState(1);
  const [selectedResultTabId,setSelectedResultTabId]=useState(null);
  const [pastedQuery,setPastedQuery]=useState(null);
  const [collections,setCollections]=useState(null);
  const [allLangs,setAllLangs]=useState([]);
  

  // ComponentDidMount
  useEffect(()=> {
    const urlParams = new URLSearchParams(window.location.search);
    const query = urlParams.get('query');
    if (page!=='__read__' && query==='__read__') {
      if (readPage()) {
        return;
      }
    }
    if (! params) {
      fetchDefaultParam();
    }
    if (! collections) {
      // fetch collections and then run parseQueryParams()
      fetchPredefinedCollections();
    }
  });

  const calcPatternSpec=(stats) => {
    // hash that will contain the result
    const pattern2spec={};
    const pattern2freq={};
    if (stats.patternStats && stats.patternStats["__global__"]) {
      // first, create a global pattern 2 freq hash
      for (var subCorpus of Object.keys(stats.patternStats)) {
        if (subCorpus !=="__global__") {
          for (var i=0;i<stats.patternStats[subCorpus].length;i++) {
            if (!pattern2freq[stats.patternStats[subCorpus][i][1]]) {
              pattern2freq[stats.patternStats[subCorpus][i][1]]=0;
            }
            pattern2freq[stats.patternStats[subCorpus][i][1]]+=stats.patternStats[subCorpus][i][2];
          }
        }
      }
      const S=stats.size["__global__"];
      // then, for each subcorpora, compute the spec
      for (subCorpus of Object.keys(stats.patternStats)) {
        if (subCorpus !=="__global__") {
          
          const s=stats.size[subCorpus];
          for (i=0;i<stats.patternStats[subCorpus].length;i++) {
            const entry=stats.patternStats[subCorpus][i][1];
            const f=stats.patternStats[subCorpus][i][2];
            const F=pattern2freq[entry];
            if (! pattern2spec[entry]) {
              pattern2spec[entry]={};
            }
            pattern2spec[entry][subCorpus]=rounded(computeLLR(f,s,F,S,true),2);
          }
        }
      }
    }
    return pattern2spec;
  };

  const readPage = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const collection = urlParams.get('collection');
    const lang = urlParams.get('lang');
    const alignedLang = urlParams.get('alignedLang');
    const fileId= urlParams.get('fileId');
    const sentNum= urlParams.get('sentNum');
    const back= urlParams.get('back');
    // deals with permalink to access the metadata and context of a sentence
    if (fileId && sentNum && lang && collection) {
      setPageData({ sentNum, fileId, lang, collection, alignedLang, back });
      setPage('read');
      return true;
    }
  }

  const fetchPredefinedCollections = () => {
    const endpoint = '/perl/xml/collections.json';
    fetch(
      process.env.REACT_APP_API_BASE + endpoint,
      { credentials: "omit" })
      .then((res) => {
        
        if (res.status !== 200) {
          console.log(`Issue reaching ${endpoint}`);
          return;
        }
        res.json().then((collections) => {
          const languages = Object.keys(collections).filter(key => key !== 'collection');
          setCollections(collections);
          setAllLangs(languages);
          parseQueryParams();
        })
      })
      .catch(() => {
        console.log(`Network error when trying to fetch ${endpoint}`);
      })
  }

  const parseQueryParams = () => {
    // Dashboard can be opened in 3 cases:
    // 1. from within the app, e.g from the home  page: no queryparams
    //    -> show type choose page
    // 2. from a new tab, after a query is made: session_id in queryparams
    //    -> run the query and show the results
    // 3. from an external app: query, collection, lang, category in queryparams
    //    -> run the query and show the results
    // 4. using an permalink to access the details of a particular sentence: sentId, collection, lang, alignedLang
    //    -> just show metadata and context

    const urlParams = new URLSearchParams(window.location.search);
    const query = urlParams.get('query');
    const collection = urlParams.get('collection');
    const predefined_corpus = urlParams.get('predefined_corpus')?urlParams.get('predefined_corpus'):urlParams.get('collection');
    const lang = urlParams.get('lang');
    const session_id = urlParams.get('session_id'); // parameter to run loadResult() - session id including query number
    //const category = urlParams.get('category');
    const sentId = urlParams.get('sentId');
    const alignedLang = urlParams.get('alignedLang');
    const generalize = urlParams.get('generalize');
    const completeSearch = urlParams.get('completeSearch');
  
    const fileId= urlParams.get('fileId');
    const sentNum= urlParams.get('sentNum');
    const back= urlParams.get('back');

    // deals with permalink to access the metadata and context of a sentence
    if (fileId && sentNum && lang && collection && query==='__read__') {
      setPageData({ sentNum, fileId, lang, collection, alignedLang, back });
      setPage('read');
      return;
    }

    // deals with permalink to access the metadata and context of a sentence
    if (sentId && lang && collection ) {
      setDisplayData({ sentId, lang, collection, alignedLang });
      setPage('displaySent');      
      return;
    }

    // allow to load the results of a given session_id (for debugging purpose)
    if (predefined_corpus && lang && session_id) {
      const corpus = {
        type: CORPUS_TYPES.PREDEFINED,
        language: lang,
        name: [predefined_corpus],
      };
      
      setCorpus(corpus);
      loadResults ({ corpus, session_id });
    }
    // deals with queries to display vocabulary and collocations statistics
    else if (predefined_corpus && lang && query ) {
      const corpus = {
        type: CORPUS_TYPES.PREDEFINED,
        language: lang,
        name: [predefined_corpus],
      };
      
      setCorpus(corpus);
      setGeneralize(generalize);
      runNewQuery({ query, corpus, generalize, completeSearch });
    }
    // if lang is not given in the parameter, search collections for predefined_corpus
    else if (predefined_corpus && query ) {
      var language;
      for (language of Object.keys(collections)) {
        if (collections[language][predefined_corpus]) {
          break;
        }
      }
      const corpus = {
        type: CORPUS_TYPES.PREDEFINED,
        language: language,
        name: [predefined_corpus],
      };

      setCorpus(corpus);
      setGeneralize(generalize);
      runNewQuery({ query, corpus, generalize, completeSearch });

    }
    // selection of predefined corpus and language
    else if (predefined_corpus && lang) {
      const corpus = {
        type: CORPUS_TYPES.PREDEFINED,
        language: lang,
        name: [predefined_corpus],
      };
      setCorpus(corpus);
      setPage('search'); 
    }
  }

  /*  How queries work (voc,colloc,results, metadata)

      runNewQuery()
        -> run the create_session_id.ajax.php query to create a new directory for the session results
        -> run loadVocab(), loadResults() or performSearch()

      loadVocab():
        -> displays the page="wait" tab (progressBar)
        -> load the json file with the vocab
        -> when the file is loaded launch addNewResultTab (creation of a new tab and addition of an entry in queryHistory
        -> run setResultInTab: update the state of queryHistory with the loaded result
      loadResults():
        -> same process for canned results
        
      performSearch()
        -> create a new result tab: addNewResultTab() -> tab without result will display progress bar
        -> launch the search.ajax.php request which launches perl asynchronously
        
      in ResultTabItem, we launch a periodic reading of fetchProgression
      when fetchProgression() returns complete, either 
        1/ run setResultInTab() if it is the first request of the tab
        2/ we run setResultHistory() and setCurrentResultIndex if it is a child query
	
  */


  // update the query history with a new tab that will include the results when they will be ready
  // for each query, all the parameters (resultType,corpus,params,generalize,completeSearch) are recorded
  // as well as the results
  const addNewResultTab = ( args ) => {
    const id=t("query")+currentQueryNum.toString();

    // WARNING HERE : the state update will not be done before the execution
    // of setResultInTab, it is why the queryHistory var is directly modified
    queryHistory.push(
      { 
        id:id, 
        corpus:args.corpus?args.corpus:corpus,
        session_id:args.session_id,
        resultType:args.resultType,
        completeSearch:args.completeSearch,
        status:"waiting",
      }
    );
    setSelectedResultTabId(id);
    setCurrentQueryNum(currentQueryNum+1);
    setPage('results');
    return id;
  };

  // once the results are ready, they are stored in queryHistory, and the status is set to ready
  const setResultInTab = (id,results) => {
    const name= results.corpusName+" - query="+results.query+(results.status?" (status="+results.status+")":"");
    for (var i=queryHistory.length-1;i>-1;i--) {
      if (queryHistory[i].id===id) {
        queryHistory[i].results=results;
        queryHistory[i].status="ready";
        queryHistory[i].name=name;
      }
    }
    setQueryHistory([...queryHistory]); // to trigger rerender 
    // the new query history has not been saved in the state yet
  };

  // Called for page 'voc' and 'colloc' - load the json and open the corresponding component
  const loadVocab = ({ corpus, query, session_id }) => {
    const resultType=(query==='__voc__')? 'voc': 'colloc';
    const corpusFileEndpoint = process.env.REACT_APP_API_BASE+'/sessions/'+session_id+"/corpus.json";
    // console.log("loadvocab, fetching",corpusFileEndpoint);
    setPage('wait');
    
    fetch(
        corpusFileEndpoint , {
        credentials: "omit",
        headers: { 'Content-Type': 'application/json' },
        method: 'GET',
      }
    )
    .then((res) => {
      if (res.status !== 200) {
        console.log(`Issue reaching ${corpusFileEndpoint}`);
        return;
      }
      res.json().then((data) => {
        //console.log(data);
        const filePath=data[resultType+'File'];
        const jsonFileEndPoint= process.env.REACT_APP_API_BASE+filePath.replace(ROOT_PATH_REGEX,"");
        fetch(
          jsonFileEndPoint , {
            credentials: "omit",
            headers: { 'Content-Type': 'application/json' },
            method: 'GET',
          }
        )
        .then((res) => {
          if (res.status !== 200) {
            console.log(`Issue reaching ${jsonFileEndPoint}`);
            return;
          }
          res.json().then((results) => {

            results.query=resultType;
            results.corpusName=corpus.name;
            results.filePath=filePath;
            // add the specificities for the patterns
            results.spec["pattern"]=calcPatternSpec(results);
            // addNewResultTab( { corpus, session_id, results, resultType } );
            const resultId= addNewResultTab( { corpus, session_id, resultType } );
            setResultInTab(resultId,results);
            
          });
        })
      });
    });
  };

  // Called for saved results - load the json and open the corresponding component
  const loadResults = ({ corpus, session_id }) => {
    const jsonFileEndPoint = process.env.REACT_APP_API_BASE+'/sessions/'+session_id+"/output.json";
    
    setPage('wait');

    // console.log("jsonFileEndPoint",jsonFileEndPoint);
    fetch(
      jsonFileEndPoint , {
        credentials: "omit",
        headers: { 'Content-Type': 'application/json' },
        method: 'GET',
      }
    )
    .then((res) => {
      if (res.status !== 200) {
        console.log(`Issue reaching ${jsonFileEndPoint}`);
        return;
      }
      res.json().then((results) => {
        // addNewResultTab( { corpus, session_id, results, resultType: "results" } );
        // pas besoin de préparer les résultats (déjà fait au moment de la sauvegarde)
        const resultId=addNewResultTab( { corpus, session_id, resultType: "results" } );
        setResultInTab(resultId,results);
      });
    })
  }


  // Run the search endpoint and load the result in state var
  // queryArgs = { sessionId, query, predefined_corpus, category, generalize, completeSearch }
  const performSearch = ( args ) => {
    const paramGeneralize=args.generalize? args.generalize: generalize;
    const paramCompleteSearch=args.completeSearch? args.completeSearch: completeSearch;

    const endpoint = '/search.ajax.php';
    const data = { 
      query:args.query,
      session_id:args.session_id, 
      generalize:paramGeneralize, 
      completeSearch:paramCompleteSearch
    };
    const resultType = (args.query === '__meta__') ? 'meta': 'results'; 
    addNewResultTab({...args, resultType });

    //console.log("EndPoint: "+process.env.REACT_APP_API_BASE + endpoint + '?' + queryParams);
    fetch(
      process.env.REACT_APP_API_BASE + endpoint, {
        credentials: process.env.REACT_APP_CREDENTIALS,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }
    )
    .then((res) => {
      if (res.status !== 200) {
        console.log(`Issue reaching ${endpoint}`);
        return;
      }
      //res.json().then((results) => {
        // if (resultType==='results') {
        //   prepareResults(results);
        // } else {
        //   results.query="meta";
        // }
        // results.corpusName=args.corpus ? args.corpus.name.join(", "):corpus.name.join(", ");
        // setResultInTab(resultId,results);
      //});
    })
  };


  // paste the query in the search field, using props (called from the statByCorpus and statGlobal components)

  const pasteQuery = useCallback((query) => {
    setPastedQuery(query);
    setPage("search");
  },[]);

  const clearPastedQuery = () => {
    setPastedQuery(null);
  }


  // Create a new search session on server, get the sessionId, and open a new window to launch the search
  // args={query,corpus,generalize,completeSearch} 
  // only query is mandatory : corpus,generalize,completeSearch are read in the state in some cases
  const runNewQuery = (args) => {
    const newCorpus= args.corpus ? args.corpus: corpus;
    const data = {
      query: args.query,
      corpus: newCorpus,
      generalize : args.generalize,
      completeSearch : args.completeSearch,
      caseInsensitive: args.caseInsensitive,
      login: (Cookies.get(FRONT_USER_FRONT_LOGGED_IN_COOKIE_NAME))?Cookies.get(FRONT_USER_FRONT_LOGGED_IN_COOKIE_NAME):"",
    };
   
    if (params) {
      // params are sent through a flat object, containing all params
      const { searchAreaParamValues, occurrencesParamValues, lexicalClassesParamValues, alrParamValues, importParamValues } = params;
      const newPercent2Parse = searchAreaParamValues.percent2Parse / 100;
      const prepSearchAreaParamValues = {...searchAreaParamValues, percent2Parse: newPercent2Parse };
      const prepParams = {
        ...prepSearchAreaParamValues, // ici les ... servent à supprimer le niveau paramValues
        ...occurrencesParamValues,
        ...lexicalClassesParamValues,
        ...alrParamValues,
        ...importParamValues,
      };

      data.params = prepParams;
    }


    // first step : creating a new query Session 
    const endpoint = '/create_session_id.ajax.php';
    fetch(
      process.env.REACT_APP_API_BASE + endpoint, {
        credentials: process.env.REACT_APP_CREDENTIALS,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }
    )
    .then((res) => {
      if (res.status !== 200) {
        console.log(`Issue reaching ${endpoint}`);
        return;
      }
      res.json().then((data) => {
        // This session is a query session, not a user session (the one
        // tracked by the session cookie).
        const session_id = data.session_id;

        if (args.query==='__voc__' || args.query==='__colloc__' ) {
          loadVocab({...args,session_id});
        } else if (args.loadResults) {
          loadResults({ corpus:newCorpus, session_id});
        } else {
          performSearch({...args,session_id});
        }
      })
    })
    .catch(() => {
      console.log(`Network error when trying to fetch ${endpoint}`);
    })

  };

  
  const handleTabChange = (navbarTabId) => {
     setPage(navbarTabId);
  }
  const handleResultTabChange = (tabId) => {
    setSelectedResultTabId(tabId);
  }

  const handleOnCorpusReady = (corpus) => {
    setCorpus(corpus);
    setPage('search');
    setParams({...params,defaultLanguage:corpus.lang});
  }

  // run when clicking on "Search" button
  const handleOnQueryReady = (query,generalize,caseInsensitive ) => {
    runNewQuery( { query, generalize, caseInsensitive } );

  }

  // run when clicking on voc / colloc / meta button in corpus description
  const handleDescriptionQuery = (query,corpus) => {
    setCorpus(corpus);
    runNewQuery({ query, corpus } );
  }

  const loadSavedResults = (resultsFilename,lang,query)=> {
    const corpus={
      type: CORPUS_TYPES.RESULTS,
      language: lang,
      name: [resultsFilename],
    }
    setCorpus(corpus);
    runNewQuery({ query, corpus, loadResults:true })
  }
 
  // run when clicking on "back to corpus selection" button
  const handleOnBackToCorpus = () => {
    setPage('corpus');
  };

  const handleLoadOldQuery = (tabId) => {
    setPage("results");
    setSelectedResultTabId(tabId);
  };

  // run when deleting a result at index index in history
  const handleDelOldQuery = (index) => {
    queryHistory.splice(index,1);
    setQueryHistory([...queryHistory]);

  };

  // same function with id as arg in closure - n must be computed when calling the resulting function
  const returnHandleOldQueryWithClosure=(id)=>{
    var closure = () => {
      var n=-1;
      for (var i in queryHistory) {
        if (queryHistory[i].id===id) {
          n=i;
        }
      }
      const newQueryHistory=[...queryHistory];
      newQueryHistory.splice(n,1);
      // changing current selected index
      var newIndex=n;
      if (n>=queryHistory.length) {
        newIndex=newIndex-1;
      }
      if (queryHistory.length>0) {
        setSelectedResultTabId(queryHistory[newIndex].id);        
        setQueryHistory(newQueryHistory);
        // bizarrement l'onglet ne se recharge pas : il faut déclencher un render
        
      } else {
        handleClearResults();
      }
    };
    return closure;
  }

  const handleClearResults = () => {
    setPage('search'); 
    setQueryHistory([]);
    setSelectedResultTabId(null);
  };

  // load the default parameters
  const fetchDefaultParam = () => {
    let endpoint= '/users/default_params.json';
    //const userFrontLoggedIn = !!(Cookies.get(FRONT_USER_FRONT_LOGGED_IN_COOKIE_NAME));
    // when a user is loggedIn try to use last_params.json
    if (userFrontLoggedIn) {
      endpoint = '/users/'+Cookies.get(FRONT_USER_FRONT_LOGGED_IN_COOKIE_NAME)+'/params/last_params.json';
    }
    
    fetch(
        process.env.REACT_APP_API_BASE + endpoint,
        { 
          method: 'GET',
          headers: { 'Cache-Control':"no-cache, no-store" } 
        }
      )
    .then((res) => {
        // if user param is not found
        if (res.status !== 200) {
          if (!userFrontLoggedIn) {
            console.log(`Issue reaching ${endpoint}`);
            return;
          }
          // if user/last_params.json is not defined, let's try /users/default_params.json
          endpoint= '/users/default_params.json';
          fetch(
            process.env.REACT_APP_API_BASE + endpoint,
            { credentials: process.env.REACT_APP_CREDENTIALS }
          ).then((res2) => {
            if (res2.status !== 200) {
              console.log(`Issue reaching ${endpoint}`);
              return;
            }
            console.log("Looking for default params");
            res2.json().then((answer) => {
              
              const searchAreaParamValues={
                maxElapsedTime: answer.maxElapsedTime,
                nbMax2Parse: answer.nbMax2Parse,
                percent2Parse: answer.percent2Parse*100,
                nbMaxFound: answer.nbMaxFound,
                completeExtraction: answer.completeExtraction,
                noDuplicate: answer.noDuplicate,
                oneMatchPerSent: answer.oneMatchPerSent,
                timeStatsSpanType: answer.timeStatsSpanType,
                timeStatsSpanLen: answer.timeStatsSpanLen,

              };
              const occurrencesParamValues= {
                nodeMaskType: answer.nodeMaskType,
                nodeMaskFeatures: answer.nodeMaskFeatures,
                collocMaskFeatures: answer.collocMaskFeatures,
                kwicNode: answer.kwicNode,
                concordSpan: answer.concordSpan,
                fullConcordSpan: answer.fullConcordSpan,
                concordSampling: answer.concordSampling,
                concordSamplingSize: answer.concordSamplingSize,
                calcConcord: answer.calcConcord,
                calcMap: answer.calcMap,
                fromPivotOnly: answer.fromPivotOnly,
                realizeOnlyOne: answer.realizeOnlyOne,
                computeEstimatedFreq: answer.computeEstimatedFreq,
                dispCriterion: answer.dispCriterion,
                otherDispCriteria: answer.otherDispCriteria,
                computeKullbackLeibler: answer.computeKullbackLeibler,
                assocMeasure: answer.assocMeasure,
                innerCollocs: answer.innerCollocs,
                minNbCooc: answer.minNbCooc,
                maxCooc: answer.maxCooc,
                calcContextTrees: answer.calcContextTrees,
                runMultivar: answer.runMultivar,
                minWordSketchLLR: answer.minWordSketchLLR,
                maxWordSketchLength: answer.maxWordSketchLength,

              };
              const alrParamValues= {
                collocCatFilter: answer.collocCatFilter,
                freqMinAlr: answer.freqMinAlr,
                dispMinAlr: answer.dispMinAlr,
                minSize: answer.minSize,
                maxSize: answer.maxSize,
                amThreshold: answer.amThreshold,
                maxNbCollocs: answer.maxNbCollocs,
                minFreqChildrenRatio4Deduplicating: answer.minFreqChildrenRatio4Deduplicating,
                useCollig: answer.useCollig,
                mergeAlr: answer.mergeAlr,
              };
              const lexicalClassesParamValues= {
                lexicalClasse:answer.lexicalClasses,
                cosSimilarityThreshold:answer.cosSimilarityThreshold,
                rel2Ignore:answer.rel2Ignore,
                cat2IgnoreLemma:answer.cat2IgnoreLemma,
              }

              const importParamValues={
                extractPatterns:answer.extractPatterns,
                extractLexicometry:answer.extractLexicometry,
                supprUnderScore:answer.supprUnderScore,
                maxSpanSize:answer.maxSpanSize,
                TTRSpanSize:answer.TTRSpanSize,
                useLemma4Repet:answer.useLemma4Repet,
                idSentPattern:answer.idSentPattern,
                timeIntPattern:answer.timeIntPattern,
                speakerPattern:answer.speakerPattern,
                metadataFormat:answer.metadataFormat,
                csvDelimiter:answer.csvDelimiter,
                noBrackets:answer.noBrackets,
                timeStatsField:answer.timeStatsField,
                timeStatsYearPattern:answer.timeStatsYearPattern,                
                timeStatsMonthPattern:answer.timeStatsMonthPattern,
                timeStatsDayPattern:answer.timeStatsDayPattern,
              };
  

              console.log("Setting default params");
              setParams({
                  searchAreaParamValues: searchAreaParamValues,
                  occurrencesParamValues: occurrencesParamValues,
                  lexicalClassesParamValues: lexicalClassesParamValues,
                  alrParamValues:alrParamValues,
                  importParamValues: importParamValues
              });
            });
          });
        } else {
        // if user param is  found
          res.json().then((answer) => {

            const searchAreaParamValues={
              maxElapsedTime: answer.maxElapsedTime,
              nbMax2Parse: answer.nbMax2Parse,
              percent2Parse: answer.percent2Parse*100,
              nbMaxFound: answer.nbMaxFound,
              completeExtraction: answer.completeExtraction ? answer.completeExtraction:0,
              noDuplicate: answer.noDuplicate ? answer.noDuplicate:0,
              oneMatchPerSent: answer.oneMatchPerSent ? answer.oneMatchPerSent:0,
              timeStatsSpanType: answer.timeStatsSpanType ? answer.timeStatsSpanType:"Y",
              timeStatsSpanLen: answer.timeStatsSpanLen ? answer.timeStatsSpanLen:1,
            };
            const occurrencesParamValues= {
              nodeMaskType: answer.nodeMaskType,
              nodeMaskFeatures: answer.nodeMaskFeatures,
              collocMaskFeatures: answer.collocMaskFeatures,
              kwicNode: answer.kwicNode,
              concordSpan: answer.concordSpan ? answer.concordSpan:40,
              fullConcordSpan: answer.fullConcordSpan ? answer.fullConcordSpan:0,
              concordSampling: answer.concordSampling,
              concordSamplingSize: answer.concordSamplingSize ? answer.concordSamplingSize:1000,
              calcConcord: answer.calcConcord ? answer.calcConcord:1,
              calcMap: answer.calcMap ? answer.calcMap:1,
              fromPivotOnly: answer.fromPivotOnly,
              realizeOnlyOne: answer.realizeOnlyOne,
              computeEstimatedFreq: answer.computeEstimatedFreq,
              dispCriterion: answer.dispCriterion,
              otherDispCriteria: answer.otherDispCriteria,
              computeKullbackLeibler: answer.computeKullbackLeibler ? answer.computeKullbackLeibler:0,
              assocMeasure: answer.assocMeasure,
              innerCollocs: answer.innerCollocs,
              minNbCooc: answer.minNbCooc,
              maxCooc: answer.maxCooc,
              calcContextTrees: answer.calcContextTrees,
              runMultivar: answer.runMultivar,
              minWordSketchLLR: answer.minWordSketchLLR ? answer.minWordSketchLLR:5,
              maxWordSketchLength: answer.maxWordSketchLength ? answer.maxWordSketchLength:15,

            };
            const alrParamValues= {
              collocCatFilter: answer.collocCatFilter? answer.collocCatFilter:"^(N|V|ADJ)",
              freqMinAlr: answer.freqMinAlr? answer.freqMinAlr:[10,10,10,10,10,10],
              dispMinAlr: answer.dispMinAlr? answer.dispMinAlr:2,
              minSize: answer.minSize? answer.minSize:3,
              maxSize: answer.maxSize? answer.maxSize:8,
              amThreshold: answer.amThreshold? answer.amThreshold:10.83,
              maxNbCollocs: answer.maxNbCollocs? answer.maxNbCollocs:30,
              minFreqChildrenRatio4Deduplicating: answer.minFreqChildrenRatio4Deduplicating? answer.minFreqChildrenRatio4Deduplicating:0.5,
              useCollig: answer.useCollig===1? 1:0,
              mergeAlr: answer.mergeAlr===1? 1:0,

            };
            const lexicalClassesParamValues= {
              lexicalClasses: answer.lexicalClasses,
              cosSimilarityThreshold: answer.cosSimilarityThreshold? answer.cosSimilarityThreshold:0.5,
              rel2Ignore:answer.rel2Ignore? answer.rel2Ignore:'cc|cconj|COORDITEMS',
              cat2IgnoreLemma: answer.cat2IgnoreLemma? answer.cat2IgnoreLemma:'PROP|NOUN|VER|ADJ',
            };
            const importParamValues={
              extractPatterns:answer.extractPatterns===1? 1:0,
              extractLexicometry:answer.extractLexicometry===1? 1:0,
              supprUnderScore:answer.supprUnderScore===1? 1:0,
              maxSpanSize:answer.maxSpanSize? answer.maxSpanSize:300,
              TTRSpanSize:answer.TTRSpanSize? answer.TTRSpanSize:1000,
              useLemma4Repet:answer.useLemma4Repet===1? 1:0,
              idSentPattern:answer.idSentPattern? answer.idSentPattern:"",
              timeIntPattern:answer.timeIntPattern? answer.timeIntPattern:"",
              speakerPattern:answer.speakerPattern? answer.speakerPattern:"",
              metadataFormat:answer.metadataFormat? answer.metadataFormat:"{author} ({year}) {title}",
              csvDelimiter:answer.csvDelimiter? answer.csvDelimiter : ";",
              noBrackets:answer.noBrackets ? answer.noBrackets : 0,
              timeStatsField:answer.timeStatsField? answer.timeStatsField:["date"],
              timeStatsYearPattern:answer.timeStatsYearPattern? answer.timeStatsYearPattern:'(\\d\\d\\d\\d)',                
              timeStatsMonthPattern:answer.timeStatsMonthPattern? answer.timeStatsMonthPattern:'\\d+\\/(\\d+)\\/\\d\\d\\d\\d',
              timeStatsDayPattern:answer.timeStatsDayPattern?answer.timeStatsDayPattern:'(\\d+)\\/\\d+\\/\\d\\d\\d\\d',
            };

            setParams({ 
                searchAreaParamValues: searchAreaParamValues,
                occurrencesParamValues: occurrencesParamValues,
                lexicalClassesParamValues: lexicalClassesParamValues,
                alrParamValues:alrParamValues,
                importParamValues:importParamValues
            });
            //console.log(params.occurrencesParamValues);

          })
        }
      })
    .catch(() => {
        console.log(`Network error when trying to fetch ${endpoint}`);
    })
  };  

  // writing the modified params in current state
  const handleOnParamReady= (obj) => {
    setParams(obj.params);
  };


  
  const corpusTab=<Corpus 
                      onCorpusReady={handleOnCorpusReady} 
                      onDescriptionQuery={handleDescriptionQuery}
                      loadSavedResults={loadSavedResults}
                      importParamValues={params? params.importParamValues:null}
                      collections={collections}
                      allLangs={allLangs}
                      pasteQuery={pasteQuery}
                  />
  const searchTab=<Search
                      onBackToCorpus={handleOnBackToCorpus}
                      onQueryReady={handleOnQueryReady}
                      corpus={corpus}
                      pastedQuery={pastedQuery}
                      pasteQuery={pasteQuery}
                      clearPastedQuery={clearPastedQuery}
                      loadSavedResults={loadSavedResults}
                  />
  const paramTab= <AdvancedParams 
                      onParamsReady={handleOnParamReady}
                      params={params}
                  />
  const historyTab= <History
                        handleLoadOldQuery={handleLoadOldQuery}
                        handleDelOldQuery={handleDelOldQuery}
                        handleClearResults={handleClearResults}
                        queryHistory={queryHistory}
                    />

  const resultTabList=queryHistory.map((q)=>{ 
        const item=<ResultTabItem 
                        id={q.id}
                        corpus={q.corpus}
                        collections={collections}
                        session_id={q.session_id}
                        results={q.results}
                        status={q.status}
                        resultType={q.resultType}
                        completeSearch={q.completeSearch}
                        pasteQuery={pasteQuery}
                        setResultInTab={setResultInTab} // called when the first results are ready
                    />;
        return  <Tab key={q.id} id={q.id} title={q.id} panel={item}>
                    <Button icon ="delete" minimal="true" onClick={returnHandleOldQueryWithClosure(q.id)} />
                </Tab>
  });

  const resultPage= <Tabs onChange={handleResultTabChange} selectedTabId={selectedResultTabId}>
                        {resultTabList}
                    </Tabs>;

  // conditionally display the components based on the progression of the user
  return (
      <>

          { page !=='read' && page !=='displaySent' && 
            <Tabs className='mainTab' large onChange={handleTabChange} selectedTabId={page}>
              <Tab id='corpus' title={t('corpusSelection')} panel={corpusTab} />
              <Tab id='search' title={t('queryDefinition')} panel={searchTab} disabled={corpus===null}/>
              <Tab id='params' title={t('advancedParams')} panel={paramTab} />
              <Tab id='results' title={t('results')} panel={resultPage} disabled={resultPage===null} />
              <Tab id='wait' title={t('')} panel={<ProgressBar/>} />

              <Tabs.Expander />
              <Tab id='history' title={t('history')} panel={historyTab} disabled={queryHistory.length===0}/>
            </Tabs>
          }

          { page === 'read' && 
            <Read 
              fileId={pageData.fileId} 
              sentNum={pageData.sentNum} 
              collection={pageData.collection} 
              lang={pageData.lang} 
              alignedLang={pageData.alignedLang}
              back={pageData.back}
            /> 
          }
          { page === 'displaySent' && 
            <DisplaySent 
              sentId={displayData.sentId} 
              collection={displayData.collection} 
              lang={displayData.lang} 
              alignedLang={displayData.alignedLang} 
            /> 
          }
      </>
    );
}

